import { useMemo, useState } from 'react';
import { createRoute, redirect, useNavigate } from '@tanstack/react-router';
import { IconPlus } from '@tabler/icons-react';
import { Route as appRoute } from '../../app.js';
import { useAuthStore } from '../../../lib/auth.js';
import { useQuotes, type QuoteListRow } from '../../../hooks/useQuotes.js';
import { canBuildQuotes, formatQuoteDate, visaSubclassDisplay, QUOTE_IMMUTABLE_NOTE } from '../../../lib/quotes.js';
import { centsToAudDisplay } from '../../../lib/money.js';
import { editorLabel } from '../../../lib/editor-label.js';
import { Button } from '../../../components/ui/Button.js';
import { Input } from '../../../components/ui/Input.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: '/quotes',
  // create_quotes + view_pricing + a tenant. The server's create_quotes
  // capability stays the real gate; canBuildQuotes documents why super_admin
  // (no tenant) and a bare offshore user (no view_pricing) are outside it.
  beforeLoad: () => {
    if (!canBuildQuotes(useAuthStore.getState().user)) throw redirect({ to: '/app' });
  },
  component: QuotesListRoute,
});

/**
 * The navigation seam. UNEXPORTED and never rendered by a test: useNavigate()
 * closes over a router that is undefined outside a RouterProvider, and this repo
 * has no router in tests. Everything below takes plain callbacks, so the page is
 * renderable bare — the ConsultantPricingPage precedent.
 */
function QuotesListRoute() {
  const navigate = useNavigate();
  return (
    <QuotesListPage
      onOpen={(quoteId) => navigate({ to: '/app/quotes/$quoteId', params: { quoteId } })}
      onNew={() => navigate({ to: '/app/quotes/new' })}
    />
  );
}

/** Live search over the loaded page — client name, email or quote number. */
function matchesQuoteQuery(q: QuoteListRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return (
    q.clientName.toLowerCase().includes(needle) ||
    q.clientEmail.toLowerCase().includes(needle) ||
    q.quoteNumber.toLowerCase().includes(needle)
  );
}

export function QuotesListPage({
  onOpen,
  onNew,
}: {
  onOpen: (quoteId: string) => void;
  onNew: () => void;
}) {
  const { data, isLoading, error, refetch } = useQuotes();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const [search, setSearch] = useState('');

  const items = useMemo(() => data?.items ?? [], [data]);
  const filtered = useMemo(() => items.filter((q) => matchesQuoteQuery(q, search)), [items, search]);

  return (
    <div className="max-w-5xl">
      <div className="mb-2 text-xs uppercase tracking-[0.14em] text-muted font-semibold">Quotes</div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl tracking-tighter2 mb-2">Quotes</h1>
          <p className="text-muted text-sm mb-6">
            Every quote your agency has issued. {QUOTE_IMMUTABLE_NOTE}
          </p>
        </div>
        <Button onClick={onNew} leftIcon={<IconPlus size={16} />}>
          New quote
        </Button>
      </div>

      <Input
        placeholder="Search client or quote number…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="mt-4">
        {isLoading && <p className="px-1 py-6 text-sm text-muted">Loading quotes…</p>}
        {error && (
          <div className="rounded-lg border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
            Couldn&apos;t load quotes: {(error as Error).message}{' '}
            <button className="underline" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        )}
        {!isLoading && !error && items.length === 0 && (
          <p className="px-1 py-6 text-sm text-muted">No quotes yet.</p>
        )}
        {!isLoading && !error && items.length > 0 && filtered.length === 0 && (
          <p className="px-1 py-6 text-sm text-muted">No quotes match.</p>
        )}
        {!isLoading && !error && filtered.length > 0 && (
          <div className="surface p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream/60 border-b border-line">
                <tr className="text-left text-muted text-xs uppercase tracking-wider">
                  <th className="px-5 py-3 font-medium">Quote</th>
                  <th className="px-5 py-3 font-medium">Date</th>
                  <th className="px-5 py-3 font-medium">Client</th>
                  <th className="px-5 py-3 font-medium">Visa</th>
                  <th className="px-5 py-3 font-medium text-right">Courses</th>
                  <th className="px-5 py-3 font-medium text-right">Total (net)</th>
                  <th className="px-5 py-3 font-medium">Issued by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {filtered.map((q) => (
                  <tr key={q.id} className="hover:bg-cream/30 transition-colors align-top">
                    <td className="px-5 py-3 whitespace-nowrap">
                      {/* A button, never a <Link>: Link dereferences the router at
                          render, which is undefined outside a RouterProvider — and
                          this page must stay renderable in a test. */}
                      <button
                        className="text-sm font-medium text-navy underline"
                        onClick={() => onOpen(q.id)}
                      >
                        {q.quoteNumber}
                      </button>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm text-ink">
                      {formatQuoteDate(q.quoteDate)}
                    </td>
                    <td className="px-5 py-3 min-w-[12rem]">
                      <span className="block text-sm text-ink">{q.clientName}</span>
                      <span className="block text-xs text-muted">{q.clientEmail}</span>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-xs text-muted">
                      {visaSubclassDisplay(q.clientVisaSubclass)}
                    </td>
                    <td className="px-5 py-3 text-right text-sm text-ink">{q.lineCount}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap text-sm font-medium text-navy">
                      {/* The SERVER's total. A QuoteListRow carries no lines[] to
                          sum, and the browser never recomputes money anyway. */}
                      {centsToAudDisplay(q.totalNetFeeCents)}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-xs text-muted">
                      {editorLabel(q, currentUserId)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
